import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import InvestorPageShell from "../../../components/Investor/InvestorPageShell";
import KycBanner from "../../../components/Investor/KycBanner";
import InvestmentCard from "../../../components/Investor/InvestmentCard";
import InvestorFilterPills from "../../../components/Investor/InvestorFilterPills";
import InvestorInvestmentsNav from "../../../components/Investor/InvestorInvestmentsNav";
import InvestorListPagination from "../../../components/Investor/InvestorListPagination";
import InvestmentRequestModal from "../../../components/Investor/InvestmentRequestModal";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { useInvestorGate } from "../../../hooks/useInvestorGate";
import { useInvestorSession } from "../../../hooks/useInvestorSession";
import { getInvestorQueryParams, withInvestorQuery } from "../../../utils/investorRouteHelpers";
import {
  useListInvestorInvestmentRequestsQuery,
  useListInvestorOpportunitiesQuery,
} from "../../../store/investor/investorInvestmentsApi";
import {
  isInvestable,
  mapInvestorOpportunityListItem,
} from "../../../utils/investorInvestmentsAdapter";
import {
  InvestorApiErrorState,
  InvestorEmptyState,
} from "../../../components/Investor/InvestorPlatformState";

const FILTERS = ["All", "Agriculture", "Palm Oil", "Real Estate", "Bond", "Cooperative", "Low", "Moderate", "High"];
const OPPORTUNITIES_PER_PAGE = 3;

export default function InvestorInvestmentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = getInvestorQueryParams(searchParams);
  const { ensureKycApproved, isKycApproved, kycStatus } = useInvestorGate();
  const { profile, activeUserId } = useInvestorSession();

  const [filter, setFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [investOpportunity, setInvestOpportunity] = useState(null);
  const [showInvestModal, setShowInvestModal] = useState(false);

  const apiParams = useMemo(() => {
    const params = { page, per_page: OPPORTUNITIES_PER_PAGE };
    if (filter !== "All") {
      if (["Low", "Moderate", "High"].includes(filter)) {
        params.risk_level = filter.toLowerCase();
      } else {
        params.category = filter;
      }
    }
    return params;
  }, [filter, page]);

  const { data: apiData, isFetching, isError, refetch } = useListInvestorOpportunitiesQuery(apiParams);
  const { data: apiRequests } = useListInvestorInvestmentRequestsQuery({ per_page: 100 });

  const myRequestCount = apiRequests?.items?.length || 0;

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    if (query.intent === "invest" && query.itemId) {
      const detailPath = `/invest/investments/${query.itemId}`;
      if (!isKycApproved) {
        navigate(
          withInvestorQuery("/invest/profile", {
            redirect: detailPath,
            intent: query.intent,
            itemId: query.itemId,
            tab: "verification",
          }),
          { replace: true }
        );
        return;
      }
      navigate(
        withInvestorQuery(detailPath, { intent: query.intent, itemId: query.itemId }),
        { replace: true }
      );
    }
  }, [query.intent, query.itemId, isKycApproved, navigate]);

  const filtered = useMemo(
    () => (apiData?.items || []).map(mapInvestorOpportunityListItem).filter(Boolean),
    [apiData?.items]
  );

  const total = apiData?.pagination?.total || filtered.length;
  const totalPages = apiData?.pagination?.last_page || 1;
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered;
  const hasFilter = filter !== "All";

  const openInvestFromCard = (opp) => {
    if (!isInvestable(opp)) {
      toast(opp.investUnavailableReason || "This opportunity isn’t open for investment yet.");
      return;
    }
    const detailPath = `/invest/investments/${opp.id}`;
    if (!ensureKycApproved({ redirect: detailPath, intent: "invest", itemId: opp.id })) {
      toast("Complete KYC to invest");
      return;
    }
    setInvestOpportunity(opp);
    setShowInvestModal(true);
  };

  let listContent = null;
  if (isFetching) {
    listContent = <TableSkeletonLoader rows={3} columns={3} />;
  } else if (isError) {
    listContent = (
      <InvestorApiErrorState
        message="We couldn’t load investment opportunities. Please try again."
        onRetry={() => refetch()}
      />
    );
  } else if (pageItems.length === 0) {
    listContent = hasFilter ? (
      <InvestorEmptyState
        title="No opportunities match this filter"
        message="Try another category or risk level, or choose All to see every available opportunity."
      />
    ) : (
      <InvestorEmptyState
        title="No opportunities available"
        message="There are no investment opportunities to browse right now. Check back soon for new offerings."
      />
    );
  } else {
    listContent = (
      <div className="row g-3">
        {pageItems.map((opp) => (
          <div key={opp.id} className="col-12 col-md-6 col-xl-4">
            <InvestmentCard opportunity={opp} onInvest={() => openInvestFromCard(opp)} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <InvestorPageShell
      title="Investments"
      subtitle="Browse vetted opportunities and submit your investment interest."
      status={kycStatus !== "approved" ? kycStatus : null}
      statusLabel={kycStatus !== "approved" ? "KYC required" : null}
    >
      <KycBanner status={kycStatus} redirectTo="/invest/investments" intent="invest" />

      <InvestorInvestmentsNav requestCount={myRequestCount} />

      <InvestorFilterPills options={FILTERS} value={filter} onChange={setFilter} />

      <div data-tour="invest-opps-catalogue">
      {listContent}

      {pageItems.length > 0 ? (
        <InvestorListPagination
          currentPage={safePage}
          perPage={OPPORTUNITIES_PER_PAGE}
          total={total}
          totalPages={totalPages}
          count={pageItems.length}
          onPageChange={setPage}
        />
      ) : null}
      </div>

      <InvestmentRequestModal
        show={showInvestModal}
        onClose={() => {
          setShowInvestModal(false);
          setInvestOpportunity(null);
        }}
        opportunity={investOpportunity}
        profile={profile}
        activeUserId={activeUserId}
        defaultUnits={investOpportunity?.minUnitsPurchase}
      />
    </InvestorPageShell>
  );
}
